import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid2';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import type { ConfigFilter } from '../../../_configs/types';
import CarTileGridSkeleton from '../../../components/car-tile/car-tile-grid-skeleton';
import checkUndefinedNullOrEmpty from '../../../utils/tools/checkUndefinedNullOrEmpty';
import type { ModuleSkeletonProps } from '../../../vars';
import filterMap from '../filter/maps/filterMap';

export default function FahrzeugsucheListTopSkeleton(props: ModuleSkeletonProps) {
    const { settings } = props;
    const theme = useTheme();
    const xsvp = useMediaQuery(theme?.breakpoints?.down('sm'));

    const indexedArray = settings?.fahrzeugsuche?.filter?.top?.map((item, index) => ({ index, value: item }));
    const filterList = filterMap(false);
    const presortedFilters = indexedArray
        ?.sort((next, curr) => {
            const currWithoutPrefix = curr?.value?.replace(/\[[A-Z]+\]/g, '') as ConfigFilter;
            const nextWithoutPrefix = next?.value?.replace(/\[[A-Z]+\]/g, '') as ConfigFilter;
            const nextIndex = next?.index;
            const currType = filterList?.[currWithoutPrefix]?.type;
            const nextType = filterList?.[nextWithoutPrefix]?.type;
            if (currType === 'range' && nextType !== 'range') {
                return -1;
            } else if (currType !== 'range' && nextType === 'range' && nextIndex % 2 === 0) {
                return -1;
            } else {
                return 0;
            }
        })
        ?.map((f) => f?.value);
    const sortedFilters = {
        direct: presortedFilters
            ?.filter((key) => (xsvp && key.startsWith('[SM]')) || (!xsvp && (key.startsWith('[SM]') || key.startsWith('[LG]'))))
            ?.map((key) => {
                const keyWithoutPrefix = key?.replace(/\[[A-Z]+\]/g, '') as ConfigFilter;
                return { filterKey: key, filterType: filterList?.[keyWithoutPrefix]?.type, element: <Skeleton key={key} variant="rounded" sx={{ height: 57.5, width: '100%' }} /> };
            })
            ?.filter((f) => !checkUndefinedNullOrEmpty(f?.element)),
    };

    return (
        <Stack gap={2}>
            <Stack direction="row" justifyContent="space-between">
                <Skeleton variant="text" sx={{ width: 150 }} />
                <Stack direction="row" gap={2} justifyContent="flex-end">
                    <Skeleton variant="rounded" sx={{ height: 35, width: 150 }} />
                    <Skeleton variant="text" sx={{ width: 200 }} />
                    <Skeleton variant="text" sx={{ width: 100 }} />
                </Stack>
            </Stack>
            <Stack data-testid="FahrzeugSucheFilterTopArea_Container" spacing={3} justifyContent="space-between">
                <Skeleton variant="rounded" sx={{ height: 57.5, width: '100%' }} />

                <Divider />

                <Stack gap={2}>
                    <Grid container spacing={2}>
                        {sortedFilters?.direct?.map((f) => (
                            <Grid key={f?.filterKey} size={{ xs: f?.filterType === 'range' ? 12 : 6, sm: 6, md: f?.filterType === 'range' ? 6 : 3 }}>
                                {f?.element}
                            </Grid>
                        ))}
                    </Grid>
                    <Box
                        display="grid"
                        gap={2}
                        gridTemplateColumns={{
                            xs: 'repeat(1, 1fr)',
                            sm: 'repeat(2, 1fr)',
                        }}
                        gridTemplateRows="repeat(1, 1fr)"
                        sx={{ maxHeight: '100%', overflow: 'visible' }}
                    >
                        <Skeleton variant="rounded" sx={{ height: 40, width: '100%' }} />
                        <Skeleton variant="rounded" sx={{ height: 40, width: '100%' }} />
                    </Box>
                </Stack>

                <Divider />
            </Stack>
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <CarTileGridSkeleton />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <CarTileGridSkeleton />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <CarTileGridSkeleton />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }} sx={{ display: { xs: 'none', sm: 'block' } }}>
                    <CarTileGridSkeleton />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }} sx={{ display: { xs: 'none', sm: 'block' } }}>
                    <CarTileGridSkeleton />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }} sx={{ display: { xs: 'none', sm: 'block' } }}>
                    <CarTileGridSkeleton />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }} sx={{ display: { xs: 'none', md: 'block' } }}>
                    <CarTileGridSkeleton />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }} sx={{ display: { xs: 'none', md: 'block' } }}>
                    <CarTileGridSkeleton />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }} sx={{ display: { xs: 'none', md: 'block' } }}>
                    <CarTileGridSkeleton />
                </Grid>
            </Grid>
        </Stack>
    );
}
